import React, { useEffect, useRef } from 'react';
import {
  DateTimePicker,
  Modal,
  Box,
  Button,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';

type PickerDate = Date | { toDate?: () => Date } | null;

export interface ScheduleDateTimePickerDialogProps {
  open: boolean;
  value?: Date | null;
  onClose: () => void;
  onConfirm: (date: Date) => void;
  showHidePage?: boolean;
  isFolder?: boolean;
  onHidePage?: () => void;
}

const normalisePickerDate = (value: PickerDate): Date | null => {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (value && typeof value.toDate === 'function') {
    const date = value.toDate();
    return Number.isFinite(date.getTime()) ? date : null;
  }

  return null;
};

const ScheduleDateTimePickerDialog = ({
  open,
  value,
  onClose,
  onConfirm,
  showHidePage = false,
  isFolder = false,
  onHidePage,
}: ScheduleDateTimePickerDialogProps) => {
  const theme = useTheme();
  const selectedDateRef = useRef<Date>(value || new Date());

  useEffect(() => {
    if (open) {
      selectedDateRef.current = value || new Date();
    }
  }, [open, value]);

  const handleChange = (date: PickerDate) => {
    const normalisedDate = normalisePickerDate(date);

    if (normalisedDate) {
      selectedDateRef.current = normalisedDate;
    }
  };

  const getNormalizedSelectedDate = () => {
    const normalizedDate = new Date(selectedDateRef.current);
    normalizedDate.setSeconds(0, 0);
    return normalizedDate;
  };

  const { main: primaryColor } = theme.palette.primary;
  const { fontFamily } = theme.typography;
  const textTransform: React.CSSProperties['textTransform'] = 'none';

  const buttonSx = {
    color: primaryColor,
    fontWeight: 600,
    fontFamily: fontFamily || 'inherit',
    fontSize: '0.875rem',
    textTransform,
    letterSpacing: 0.3,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      border: 'none',
      boxShadow: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${primaryColor}`,
      outlineOffset: 2,
    },
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-date-time-title"
        data-testid="schedule-date-time-dialog"
        sx={{
          borderRadius: 2,
          boxShadow: theme.shadows[24],
          width: 320,
          maxWidth: '95vw',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          color: 'text.primary',
          outline: 'none',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            px: 2,
            pt: 1.5,
            pb: 0,
          }}
        >
          <Typography
            id="schedule-date-time-title"
            component="h2"
            sx={{
              color: 'text.secondary',
              fontFamily,
              fontWeight: 500,
              fontSize: '12px',
              letterSpacing: '0.08em',
            }}
          >
            Select date &amp; time
          </Typography>
        </Box>

        <DateTimePicker
          key={open ? 'open' : 'closed'}
          kinds="pastDisabled"
          value={value || new Date()}
          onChange={handleChange}
          sx={{
            width: '100%',
            boxShadow: 'none',
            '& .MuiPickersDay-root.Mui-selected:hover': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: 2,
            pb: 1.5,
            pt: 0.5,
            bgcolor: 'background.paper',
          }}
        >
          {showHidePage && (
            <Button
              kind="secondary"
              type="button"
              onClick={() => {
                if (onHidePage) onHidePage();
                onClose();
              }}
              sx={{ ...buttonSx, mr: 'auto' }}
            >
              {isFolder ? 'Hide section' : 'Hide page'}
            </Button>
          )}
          <Button
            kind="secondary"
            type="button"
            onClick={onClose}
            sx={buttonSx}
          >
            Cancel
          </Button>
          <Button
            kind="secondary"
            type="button"
            onClick={() => {
              onConfirm(getNormalizedSelectedDate());
              onClose();
            }}
            sx={buttonSx}
          >
            Schedule
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ScheduleDateTimePickerDialog;
